/**
 * `useVoiceCapture` — record a voice memo with LIVE captions, then store it
 * through the normal media-v3 upload and receive the authoritative transcript.
 *
 * Two legs run at once while the user talks:
 *  - live leg: media-v3 mints a short-lived STT grant → the browser opens its
 *    own vendor WebSocket and streams 16 kHz PCM16 frames from a tap on the mic
 *    → partial/final events fold into `live` (captions ~300 ms behind speech);
 *  - storage leg: `MediaRecorder` on the same mic produces the container file
 *    that `useMediaUpload` sends to S3 at stop (unchanged pipeline:
 *    init → PUT → final → box).
 *
 * At stop the live transcript rides the upload as the attachment's DRAFT — posted
 * right after `/init`, BEFORE any byte goes up, so it is in the database when
 * `/final` dispatches processing (which then announces the draft back as
 * `transcript.ready` instead of transcribing again). The live leg is strictly
 * additive: any failure in it (no grant, no AudioWorklet, vendor down, socket
 * drop) degrades to the server-side transcription.
 */

import { useAuth } from "@clerk/clerk-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { env } from "@/env";
import { useRealtimeRelay } from "@/features/shared/realtime-relay";
import { fetchSttGrant, type MediaClientConfig } from "../_api/media-v3-client";
import {
  type AudioRecorderHandle,
  startAudioRecorder,
} from "../_lib/audio-recorder";
import {
  createLiveTranscriber,
  type LiveTranscriber,
} from "../_lib/live-transcript/live-transcriber";
import {
  applyTranscriptEvent,
  committedTranscriptText,
  EMPTY_LIVE_TRANSCRIPT,
  type LiveTranscript,
} from "../_lib/live-transcript/transcript-merge";
import {
  createPcm16AudioContext,
  type Pcm16CaptureHandle,
  startPcm16Capture,
} from "../_lib/pcm16-capture";
import type { MediaUploadJob } from "../_types/media-upload";
import { useMediaUpload } from "./use-media-upload";

/** Clerk JWT template the media service validates against. */
const CLERK_TOKEN_TEMPLATE = "birdwatch-template";
/** PCM frame length sent per socket message (vendor window: 50–1000 ms). */
const PCM_FRAME_MS = 100;
/** Frames held while no socket exists (grant in flight, reconnecting): 30 s. */
const MAX_PENDING_FRAMES = 30_000 / PCM_FRAME_MS;
/** Fresh-grant reconnects allowed per recording after the vendor drops us. */
const MAX_RECONNECTS = 2;

export type VoiceCapturePhase =
  | "idle"
  | "recording"
  | "uploading"
  | "transcribing"
  | "done"
  | "failed";

export type LiveCaptionStatus = "off" | "connecting" | "live" | "unavailable";

export interface UseVoiceCaptureResult {
  phase: VoiceCapturePhase;
  /** Whether live captions are streaming for the current recording. */
  liveStatus: LiveCaptionStatus;
  /** Why the live leg is unavailable (grant error, socket close reason) — null while healthy. */
  liveError: string | null;
  /** Live captions: committed segments + the still-revisable tail. */
  live: { committed: string; pending: string };
  /** `provider/model` tag of the live recognizer once a grant was issued, else null. */
  liveModel: string | null;
  /** Authoritative transcript (full-file pass) once the relay delivers it. */
  final: { text: string; model: string | null } | null;
  /** Whether the live transcript was stored as the draft (null until known). */
  draftStored: boolean | null;
  attachmentId: string | null;
  /** The completed recording, for playback. */
  recording: File | null;
  /** The open microphone stream while recording (for a waveform), else null. */
  stream: MediaStream | null;
  elapsedS: number;
  error: string | null;
  /** The underlying upload job (progress, status). */
  job: MediaUploadJob | null;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  reset: () => void;
}

export function useVoiceCapture(): UseVoiceCaptureResult {
  const { getToken } = useAuth();
  const { jobs, upload, reset: resetUploads } = useMediaUpload();

  const [phase, setPhase] = useState<VoiceCapturePhase>("idle");
  const [liveStatus, setLiveStatus] = useState<LiveCaptionStatus>("off");
  const [liveError, setLiveError] = useState<string | null>(null);
  const [liveModel, setLiveModel] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<LiveTranscript>(
    EMPTY_LIVE_TRANSCRIPT,
  );
  const [final, setFinal] = useState<UseVoiceCaptureResult["final"]>(null);
  const [draftStored, setDraftStored] = useState<boolean | null>(null);
  const [recording, setRecording] = useState<File | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [elapsedS, setElapsedS] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const recorderRef = useRef<AudioRecorderHandle | null>(null);
  const pcmRef = useRef<Pcm16CaptureHandle | null>(null);
  const transcriberRef = useRef<LiveTranscriber | null>(null);
  const transcriptRef = useRef<LiveTranscript>(EMPTY_LIVE_TRANSCRIPT);
  const pendingFramesRef = useRef<ArrayBuffer[]>([]);
  const capturingRef = useRef(false);
  /** Bumped per recording (and on unmount) so a late async step from an earlier one bails. */
  const sessionRef = useRef(0);
  const reconnectsRef = useRef(0);
  /**
   * True once the live text is known to be missing audio (frames dropped,
   * reconnects exhausted, the tap interrupted, or the vendor not flushed).
   * A degraded transcript is shown but NOT stored as the draft — the stored
   * draft suppresses the server's full-file pass, which must then run.
   */
  const liveDegradedRef = useRef(false);
  const liveModelRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const job = fileName
    ? (jobs.find((j) => j.fileName === fileName) ?? null)
    : null;
  const attachmentId = job?.attachmentId ?? null;

  const clientConfig = useCallback(async (): Promise<MediaClientConfig> => {
    const token = await getToken({ template: CLERK_TOKEN_TEMPLATE });
    return { baseUrl: env.PUBLIC_MEDIA_SERVICE_URL, token: token ?? "" };
  }, [getToken]);

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  /** Whatever the recognizer still holds is stable text on this model — keep it. */
  const commitPendingTail = () => {
    if (!transcriptRef.current.pending) return;
    transcriptRef.current = applyTranscriptEvent(transcriptRef.current, {
      kind: "final",
      text: transcriptRef.current.pending,
    });
    setTranscript(transcriptRef.current);
  };

  /** Mint a grant and open the live stream; a failure leaves recording intact. */
  const connectLive = useCallback(async () => {
    const session = sessionRef.current;
    const pcm = pcmRef.current;
    if (!pcm) return;
    setLiveStatus("connecting");
    let grant: Awaited<ReturnType<typeof fetchSttGrant>>;
    try {
      grant = await fetchSttGrant(await clientConfig());
    } catch (e) {
      if (session !== sessionRef.current) return;
      // No socket from here on — the tap's frames have nowhere to go, so the
      // live text cannot cover the rest of the memo (and, after a drop, no
      // further onDrop can ever mark it). Don't store it as the draft.
      liveDegradedRef.current = true;
      setLiveStatus("unavailable");
      setLiveError(`grant: ${(e as Error).message}`);
      return;
    }
    // The recording this grant was for is over (or replaced) — don't bind it.
    if (session !== sessionRef.current || !capturingRef.current) return;

    const transcriber = createLiveTranscriber(
      { grant, sampleRate: pcm.sampleRate, frameMs: PCM_FRAME_MS },
      {
        onEvent(event) {
          if (session !== sessionRef.current) return;
          transcriptRef.current = applyTranscriptEvent(
            transcriptRef.current,
            event,
          );
          setTranscript(transcriptRef.current);
        },
        onOpen() {
          if (session === sessionRef.current) setLiveStatus("live");
        },
        onDrop(reason) {
          if (session !== sessionRef.current) return;
          transcriberRef.current = null;
          if (!capturingRef.current) return;
          // A new session starts a new turn: bank the turn in progress first.
          commitPendingTail();
          setLiveError(`stream dropped: ${reason}`);
          if (reconnectsRef.current >= MAX_RECONNECTS) {
            liveDegradedRef.current = true;
            setLiveStatus("unavailable");
            return;
          }
          reconnectsRef.current += 1;
          // Grants are short-lived and documented single-use — mint a fresh one.
          void connectLive();
        },
      },
    );
    liveModelRef.current = `${grant.provider}/${grant.model}`;
    setLiveModel(liveModelRef.current);
    // PCM frames are self-describing: replaying the gap needs no header.
    for (const frame of pendingFramesRef.current) transcriber.send(frame);
    pendingFramesRef.current = [];
    transcriberRef.current = transcriber;
  }, [clientConfig]);

  const start = useCallback(async () => {
    if (capturingRef.current) return;
    sessionRef.current += 1;
    const session = sessionRef.current;
    // Synchronously, inside the user gesture: iOS Safari will not start a
    // context created after the (awaited) mic prompt.
    let audioContext: AudioContext | null = null;
    let tapError: string | null = null;
    try {
      audioContext = createPcm16AudioContext();
    } catch (e) {
      tapError = (e as Error).message;
    }

    resetUploads();
    setPhase("recording");
    setError(null);
    setLiveError(null);
    setLiveModel(null);
    setFinal(null);
    setDraftStored(null);
    setRecording(null);
    setFileName(null);
    setElapsedS(0);
    transcriptRef.current = EMPTY_LIVE_TRANSCRIPT;
    setTranscript(EMPTY_LIVE_TRANSCRIPT);
    pendingFramesRef.current = [];
    reconnectsRef.current = 0;
    liveDegradedRef.current = false;
    liveModelRef.current = null;
    capturingRef.current = true;

    let recorder: AudioRecorderHandle;
    try {
      recorder = await startAudioRecorder();
    } catch (e) {
      void audioContext?.close().catch(() => undefined);
      // Denied/failed after the user already abandoned this recording: no error.
      if (session !== sessionRef.current || !capturingRef.current) return;
      capturingRef.current = false;
      setPhase("failed");
      setError(`Microphone error: ${(e as Error).message}`);
      return;
    }
    if (session !== sessionRef.current || !capturingRef.current) {
      // Reset/unmount while the mic prompt was up — release what we got.
      void recorder.stop();
      void audioContext?.close().catch(() => undefined);
      return;
    }
    recorderRef.current = recorder;
    setStream(recorder.stream);
    timerRef.current = setInterval(() => setElapsedS((s) => s + 1), 1000);

    // The PCM tap is the live leg's input; without it the memo still records.
    if (!audioContext) {
      setLiveStatus("unavailable");
      setLiveError(`audio tap: ${tapError}`);
      return;
    }
    let pcm: Pcm16CaptureHandle;
    try {
      pcm = await startPcm16Capture(recorder.stream, {
        context: audioContext,
        frameMs: PCM_FRAME_MS,
        onFrame(frame) {
          // A tail flushed by a torn-down tap must not seed the next recording.
          if (session !== sessionRef.current) return;
          if (transcriberRef.current) {
            transcriberRef.current.send(frame);
            return;
          }
          const pending = pendingFramesRef.current;
          if (pending.length >= MAX_PENDING_FRAMES) {
            pending.shift();
            liveDegradedRef.current = true;
          }
          pending.push(frame);
        },
        onStateChange(state) {
          if (session !== sessionRef.current || !capturingRef.current) return;
          if (state === "running") return;
          // An interruption (call, Siri, background) left a hole in the audio.
          liveDegradedRef.current = true;
          setLiveError(`audio tap ${state}`);
        },
      });
    } catch (e) {
      if (session !== sessionRef.current) return;
      setLiveStatus("unavailable");
      setLiveError(`audio tap: ${(e as Error).message}`);
      return;
    }
    if (session !== sessionRef.current || !capturingRef.current) {
      void pcm.stop();
      return;
    }
    pcmRef.current = pcm;
    // Don't block the mic on the grant — frames buffer until the socket opens.
    void connectLive();
  }, [connectLive, resetUploads]);

  const stop = useCallback(async () => {
    if (!capturingRef.current) return;
    const recorder = recorderRef.current;
    if (!recorder) {
      // The mic prompt is still up — abandon the recording; start() releases
      // the mic and the context when the prompt resolves.
      capturingRef.current = false;
      stopTimer();
      setPhase("idle");
      return;
    }
    const session = sessionRef.current;
    capturingRef.current = false;
    recorderRef.current = null;
    stopTimer();
    setPhase("uploading");

    const recorded = recorder.stop();
    // Order matters: the tap flushes its sub-frame tail INTO the transcriber,
    // which then asks the vendor to flush trailing finals. Nothing here may
    // reject — a stranded "uploading" phase would lock the mic button.
    const pcm = pcmRef.current;
    pcmRef.current = null;
    await pcm?.stop().catch(() => undefined);
    const transcriber = transcriberRef.current;
    transcriberRef.current = null;
    pendingFramesRef.current = [];
    // No transcriber at stop while a live model exists means the stream died
    // and its reconnect had not landed — the audio since then reached nobody.
    const flushed = transcriber
      ? await transcriber.stop()
      : !liveModelRef.current;
    const captured = await recorded;
    // A reset/unmount during those waits owns the state now — and has already
    // released everything; the recording is simply discarded.
    if (session !== sessionRef.current) return;
    if (!flushed) {
      liveDegradedRef.current = true;
      setLiveError("live transcript not flushed by the vendor");
    }
    setStream(null);
    setLiveStatus("off");

    commitPendingTail();
    const text = committedTranscriptText(transcriptRef.current);
    const model = liveModelRef.current;
    // A transcript with a known hole stays on screen but is not stored: the
    // stored draft would suppress the server's full-file transcription.
    const draft =
      text && model && !liveDegradedRef.current ? { text, model } : undefined;

    setRecording(captured.file);
    setFileName(captured.file.name);
    upload([captured.file], draft ? { transcriptDraft: draft } : undefined);
    setPhase("transcribing");
  }, [upload]);

  /** Release mic, tap, and socket without waiting — for reset and unmount. */
  const teardown = () => {
    capturingRef.current = false;
    sessionRef.current += 1;
    stopTimer();
    void recorderRef.current?.stop();
    recorderRef.current = null;
    void pcmRef.current?.stop();
    pcmRef.current = null;
    void transcriberRef.current?.stop();
    transcriberRef.current = null;
    pendingFramesRef.current = [];
  };

  const reset = useCallback(() => {
    teardown();
    resetUploads();
    setStream(null);
    setPhase("idle");
    setLiveStatus("off");
    setLiveError(null);
    setLiveModel(null);
    liveModelRef.current = null;
    setTranscript(EMPTY_LIVE_TRANSCRIPT);
    transcriptRef.current = EMPTY_LIVE_TRANSCRIPT;
    setFinal(null);
    setDraftStored(null);
    setRecording(null);
    setFileName(null);
    setElapsedS(0);
    setError(null);
  }, [resetUploads]);

  // Whether the draft landed is the upload pipeline's word (the `draft` event).
  useEffect(() => {
    if (job?.draftStored == null) return;
    setDraftStored(job.draftStored);
  }, [job?.draftStored]);

  // The upload itself died — terminal; no transcript event is coming.
  useEffect(() => {
    if (job?.phase !== "failed") return;
    setPhase("failed");
    setError(job.error ?? "upload failed");
  }, [job?.phase, job?.error]);

  useRealtimeRelay({
    channels: attachmentId ? [attachmentId] : [],
    events: ["transcript.ready", "transcript.failed"],
    onData(payload) {
      if (payload.event === "transcript.ready") {
        setFinal({
          text: payload.data.text,
          model: payload.data.model ?? null,
        });
        setPhase("done");
        return;
      }
      // Processing finished without an authoritative transcript — the stored
      // draft (if any) stands; surface the reason without failing the capture.
      setPhase("done");
      setError(payload.data.reason || "transcription failed");
    },
  });

  // Unmounting mid-recording must not leave the mic on or a vendor session open.
  useEffect(() => teardown, []);

  return {
    phase,
    liveStatus,
    liveError,
    live: {
      committed: committedTranscriptText(transcript),
      pending: transcript.pending,
    },
    liveModel,
    final,
    draftStored,
    attachmentId,
    recording,
    stream,
    elapsedS,
    error,
    job,
    start,
    stop,
    reset,
  };
}
